import type { AssistantSelectableOption } from '@cdf/api-client-react';

const MAX_OPTIONS = 8;

function createOption(raw: string): AssistantSelectableOption | null {
  const label = raw.trim().replace(/^[“"']|[”"']$/g, '').replace(/[.;:,?!]+$/, '').trim();
  if (label.length < 2 || label.length > 80 || label.split(/\s+/).length > 8) return null;
  if (/\b(if you want|i can|please|let me|would you)\b/i.test(label)) return null;
  return {
    id: `text:${label.toLowerCase()}`,
    label,
    message: `I choose ${label}.`
  };
}

/**
 * Make plain-language choices clickable when the assistant did not return
 * structured options. Clicking only sends a reply; protected actions still
 * require their normal confirmation.
 */
export function parseTextSelectableOptions(content: string): AssistantSelectableOption[] {
  const seen = new Set<string>();
  const options: AssistantSelectableOption[] = [];
  const add = (raw: string) => {
    const option = createOption(raw);
    if (!option || seen.has(option.id) || options.length >= MAX_OPTIONS) return;
    seen.add(option.id);
    options.push(option);
  };

  for (const line of content.split(/\r?\n/)) {
    const match = line.match(/^\s*(?:[-*•]|\d+[.)])\s+(.+?)\s*$/);
    if (match?.[1]) add(match[1]);
  }
  if (options.length >= 2) return options;

  // A short, explicit choice question may put its options in one sentence.
  const question = content.split(/\r?\n/).map((line) => line.trim()).reverse()
    .find((line) => /\?$/.test(line) && /\bor\b/i.test(line) &&
      /^(which|what|would you prefer|do you prefer|choose|select)\b/i.test(line));
  const choiceText = question?.match(/(?:—|:|\bbetween\b|\bprefer\b)\s*(.+)\?$/i)?.[1];
  if (!choiceText || choiceText.length > 300) return options;

  const parts = choiceText.split(/\s*,\s*(?:or\s+)?|\s+or\s+/i);
  if (parts.length < 2 || parts.length > MAX_OPTIONS) return options;
  options.length = 0;
  seen.clear();
  for (const part of parts) add(part);
  return options.length >= 2 ? options : [];
}
